import { Field, Form } from 'formik';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ─── Page Layout ─── */

export const PageContainer = styled.div`
  padding: 6rem 0 4rem;
  min-height: 80vh;
  background-color: var(--bg-primary);
`;

export const ContentContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const Title = styled.h1`
  font-family: var(--font-heading);
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  text-align: center;
`;

export const Subtitle = styled.p`
  color: var(--text-muted);
  font-size: 1.15rem;
  max-width: 700px;
  margin: 0 auto 3rem;
  text-align: center;
`;

/* ─── Booking Layout ─── */

export const BookingLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const BookingSection = styled.div`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
`;

export const SectionTitle = styled.h2`
  background-color: var(--bg-elevated);
  color: var(--text-primary);
  margin: 0;
  padding: 1.2rem 1.5rem;
  font-family: var(--font-heading);
  font-size: 1.3rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--border-color);

  svg {
    margin-right: 12px;
    color: var(--accent-teal);
  }
`;

export const SectionContent = styled.div`
  padding: 1.5rem;
`;

/* ─── Calendar ─── */

export const CalendarContainer = styled.div`
  .react-datepicker {
    width: 100%;
    border: none;
    box-shadow: none;
    font-family: var(--font-body);
    background: var(--bg-card);
    color: var(--text-secondary);
  }

  .react-datepicker__month-container {
    width: 100%;
    background: var(--bg-card);
  }

  .react-datepicker__month {
    background: var(--bg-card);
    margin: 0;
    padding: 0.4rem;
  }

  .react-datepicker__header {
    background-color: var(--bg-elevated);
    border-bottom: 1px solid var(--border-color);
    border-radius: 0;
  }

  .react-datepicker__current-month {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 1.1rem;
    padding: 8px 0;
  }

  .react-datepicker__day-name {
    color: var(--accent-teal);
    font-weight: 600;
    width: 2.5rem;
    margin: 0.2rem;
    font-size: 0.8rem;
    text-transform: uppercase;
  }

  .react-datepicker__week {
    background: var(--bg-card);
  }

  .react-datepicker__day {
    width: 2.5rem;
    height: 2.5rem;
    line-height: 2.5rem;
    margin: 0.2rem;
    border-radius: 50%;
    color: var(--text-secondary);
    transition: all var(--transition-fast);
  }

  .react-datepicker__day:hover {
    background-color: var(--bg-elevated);
    color: var(--text-primary);
  }

  .react-datepicker__day--selected {
    background: var(--gradient-gold) !important;
    color: var(--text-inverse) !important;
    font-weight: 700;
  }

  .react-datepicker__day--today {
    border: 1px solid var(--accent-teal);
    font-weight: 600;
  }

  .react-datepicker__day--disabled {
    color: var(--text-muted) !important;
    opacity: 0.3;
    text-decoration: line-through;
  }

  .react-datepicker__navigation {
    top: 12px;
  }

  .react-datepicker__navigation-icon::before {
    border-color: var(--text-secondary);
  }
`;

export const Legend = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: var(--text-muted);
`;

export const LegendColor = styled.span<{ $bgColor: string }>`
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ $bgColor }) => $bgColor};
  margin-right: 6px;
  border: 1px solid var(--border-color);
`;

/* ─── Form ─── */

export const BookingForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Label = styled.label`
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
  display: flex;
  align-items: center;

  svg {
    margin-right: 8px;
    color: var(--accent-teal);
  }
`;

export const StyledField = styled(Field)`
  padding: 0.8rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 1rem;
  font-family: var(--font-body);
  color: var(--text-primary);
  background-color: var(--bg-primary);
  transition: all var(--transition-fast);

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--accent-teal);
    box-shadow: 0 0 0 2px rgba(0, 201, 177, 0.15);
  }
`;

export const StyledSelect = styled(Field)`
  padding: 0.8rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 1rem;
  font-family: var(--font-body);
  color: var(--text-primary);
  background-color: var(--bg-primary);
  transition: all var(--transition-fast);

  &:focus {
    outline: none;
    border-color: var(--accent-teal);
    box-shadow: 0 0 0 2px rgba(0, 201, 177, 0.15);
  }
`;

export const ErrorText = styled.div`
  color: var(--accent-coral);
  font-size: 0.85rem;
  margin-top: 0.4rem;
`;

export const SubmitButton = styled.button`
  background: var(--gradient-gold);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--border-radius);
  padding: 1rem;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 1rem;
  transition: all var(--transition-base);
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: var(--shadow-glow-gold);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(244, 170, 0, 0.35);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    margin-right: 8px;
  }
`;

/* ─── Charter Option Cards ─── */

export const CharterOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.8rem;
  margin-bottom: 0.5rem;
`;

export const CharterOption = styled.div<{ $selected?: boolean }>`
  border: 2px solid ${({ $selected }) => $selected ? 'var(--accent-teal)' : 'var(--border-color)'};
  background-color: ${({ $selected }) => $selected ? 'rgba(0, 201, 177, 0.06)' : 'var(--bg-primary)'};
  border-radius: var(--border-radius);
  padding: 1rem;
  cursor: pointer;
  transition: all var(--transition-base);
  position: relative;

  &:hover {
    border-color: var(--accent-teal);
  }

  ${({ $selected }) => $selected && `
    &::after {
      content: '✓';
      position: absolute;
      top: -8px;
      right: -8px;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: var(--gradient-accent);
      display: flex;
      justify-content: center;
      align-items: center;
      color: white;
      font-size: 0.7rem;
      font-weight: 700;
    }
  `}
`;

export const OptionTitle = styled.h4`
  margin: 0 0 0.4rem;
  font-family: var(--font-heading);
  font-size: 0.95rem;
  color: var(--text-primary);
`;

export const OptionDetails = styled.div`
  font-size: 0.85rem;
  color: var(--text-muted);

  div {
    display: flex;
    align-items: center;
    margin-bottom: 0.2rem;

    svg {
      margin-right: 6px;
      color: var(--accent-teal);
      flex-shrink: 0;
    }
  }
`;

/* ─── Modal ─── */

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

export const ModalContent = styled.div`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 500px;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const SuccessMessage = styled.div`
  padding: 2.5rem;
  text-align: center;

  h3 {
    font-family: var(--font-heading);
    color: var(--text-primary);
    font-size: 1.5rem;
    margin-bottom: 0.8rem;
  }

  p {
    color: var(--text-secondary);
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }
`;

export const CloseButton = styled.button`
  background: var(--gradient-gold);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--border-radius);
  padding: 0.8rem 2rem;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all var(--transition-base);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-glow-gold);
  }
`;

export const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(0, 201, 177, 0.1);
  border: 2px solid var(--accent-teal);
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 1.5rem;

  svg {
    font-size: 2.5rem;
    color: var(--accent-teal);
  }
`;

export const WarningIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(224, 168, 0, 0.1);
  border: 2px solid #d4a017;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 1.5rem;

  svg {
    font-size: 2.5rem;
    color: #d4a017;
  }
`;
